import React, { useEffect, useContext, useState, useRef } from 'react';

import dayjs from 'dayjs';
import { doc, collection, collectionGroup, onSnapshot, query, where, getDoc, orderBy, getDocs, getCountFromServer } from 'firebase/firestore';
import { dbFirebase, storage } from '../../config';
import { ref, getDownloadURL } from 'firebase/storage';

import Grid from '@mui/material/Unstable_Grid2/Grid2';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AppContext } from '../../contexts-api/AppContextProvider';
import { Typography, Button } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import {
  waitingEdl,
  createEdl,
  deleteEdl,
  modifyEdl,
  modifyAnnexesEdl,
  acceptRefuseEdl,
  acceptRefuseReportEdl,
  refuseEdlByAdmin,
  updateEdlDate,
} from '../../api-service-customer';
import ViewItem from './ViewItem';

import DialogSky from '../components/DialogSky';
import ViewEDL from '../components/ViewEDL';

import ViewCalendar from './ViewCalendar';
import getDefaultValues from '../newEDL/defaultValue';

import SelectSocietyAdmin from './SelectSocietyAdmin';
import SelectProviderAdmin from './SelectProviderAdmin';
import SelectBookingProvider from './SelectBookingProvider';
import DialogRefuse from './DialogRefuse';
import DialogFinishEDL from './DialogFinishEDL';
import DialogModifyAnnexesAdmin from './DialogModifyAnnexesAdmin';
import DialogDeleteAssignEdlCustomer from './DialogDeleteAssignEdlCustomer';
import DialogUpdateDateAdmin from './DialogUpdateDateAdmin';
import { cloneDeep } from 'lodash';

const _getPrivateInfosAdmin = async (docRef) => {
  const pathRef = docRef.path + '/privateA/refusByProviders';
  const infos = await getDoc(doc(dbFirebase, pathRef));
  return infos.exists() ? infos.data() : null;
};

const _getPricing = async (listPricing, docRef) => {
  const results = {};
  const promises = listPricing.map(async (pathPrivate) => {
    const pathRef = docRef.path + '/' + pathPrivate + '/pricing';
    const docPricing = await getDoc(doc(dbFirebase, pathRef));
    if (pathPrivate === 'privateC') results['customer'] = docPricing.data();
    if (pathPrivate === 'privateP' && docPricing.exists()) results['provider'] = docPricing.data();
  });
  await Promise.all(promises); // Attendre que toutes les requêtes soient terminées
  return results;
};


// Fonction pour compter le nombre total d'EDLs
async function countTotalEdls(nameCompany, profil) {
  const typeCompany = profil.company.type;
  const status = ['submit', 'waiting', 'assign', 'control', 'finish'];

  try {
    let q = null;
    if (['customer', 'admin'].includes(typeCompany)) {
      const baseCollection = collection(dbFirebase, 'edls', nameCompany, 'listEdls');
      if (profil.user.profil !== 'administrator') {
        q = query(
          baseCollection,
          where('metadatas.status', 'in', status),
          where('customer.user.uid', '==', profil.user.uid),
        );
      } else {
        q = query(
          baseCollection,
          where('metadatas.status', 'in', status),
        );
      }
    } else if (['provider'].includes(typeCompany)) {
      if (profil.user.profil !== 'administrator') {
        q = query(
          collectionGroup(dbFirebase, 'privateP'),
          where('uid', '==', profil.user.uid),
        );
      } else {
        q = query(
          collectionGroup(dbFirebase, 'privateP'),
          where('company', '==', profil.company.name),
        );
      }
    }

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error('Erreur lors du comptage des EDLs:', error);
    return 0;
  }
}

// Fonction pour charger les EDLs d'un mois spécifique
async function loadEdlsForMonth(nameCompany, allEdls, profil, monthStart, monthEnd) {
  const typeCompany = profil.company.type;
  const status = ['submit', 'waiting', 'assign', 'control', 'finish'];
  const loadedEdls = [];

  try {
    let q = null;
    if (['customer', 'admin'].includes(typeCompany)) {
      const baseCollection = collection(dbFirebase, 'edls', nameCompany, 'listEdls');
      if (profil.user.profil !== 'administrator') {
        q = query(
          baseCollection,
          where('metadatas.status', 'in', status),
          where('customer.user.uid', '==', profil.user.uid),
          where('manage.dateEDL', '>=', monthStart),
          where('manage.dateEDL', '<=', monthEnd),
          orderBy('manage.dateEDL', 'desc'),
        );
      } else {
        q = query(
          baseCollection,
          where('metadatas.status', 'in', status),
          where('manage.dateEDL', '>=', monthStart),
          where('manage.dateEDL', '<=', monthEnd),
          orderBy('manage.dateEDL', 'desc'),
        );
      }
    } else if (['provider'].includes(typeCompany)) {
      if (profil.user.profil !== 'administrator') {
        q = query(
          collectionGroup(dbFirebase, 'privateP'),
          where('uid', '==', profil.user.uid),
          where('dateEDL', '>=', monthStart),
          where('dateEDL', '<=', monthEnd),
          orderBy('dateEDL', 'desc'),
        );
      } else {
        q = query(
          collectionGroup(dbFirebase, 'privateP'),
          where('company', '==', profil.company.name),
          where('dateEDL', '>=', monthStart),
          where('dateEDL', '<=', monthEnd),
          orderBy('dateEDL', 'desc'),
        );
      }
    }

    const snapshot = await getDocs(q);

    for (const docSnap of snapshot.docs) {
      let idEdl, datas, docRef;

      if (typeCompany === 'provider') {
        const docRefEdl = doc(dbFirebase, docSnap.ref.parent.parent.path);
        const docEdl = await getDoc(docRefEdl);
        datas = docEdl.data();
        docRef = docEdl.ref;
        idEdl = docEdl.id;
      } else {
        idEdl = docSnap.id;
        datas = docSnap.data();
        docRef = docSnap.ref;
      }

      // Éviter les doublons (déjà en cache)
      if (allEdls.current[idEdl]) {
        continue;
      }

      const newEdl = { [idEdl]: datas };

      // Fetch pricing
      const listPricing = [];
      if (typeCompany === 'customer') listPricing.push('privateC');
      if (typeCompany === 'admin') listPricing.push('privateC', 'privateP');
      if (typeCompany === 'provider') listPricing.push('privateP');
      const pricingUser = await _getPricing(listPricing, docRef);
      Object.keys(pricingUser).forEach((tc) => {
        newEdl[idEdl][tc] = {
          ...newEdl[idEdl][tc],
          pricing: pricingUser[tc],
        };
      });

      // Fetch refus by provider for admin
      let privateAdmin = null;
      if (typeCompany === 'admin') privateAdmin = await _getPrivateInfosAdmin(docRef);
      if (privateAdmin !== null)
        newEdl[idEdl] = {
          ...newEdl[idEdl],
          admin: { refusByProviders: privateAdmin },
        };

      // Status mapping for customer/provider
      if (['customer', 'provider'].includes(typeCompany)) {
        if (newEdl[idEdl].metadatas.status === 'waiting') newEdl[idEdl].metadatas.status = 'submit';
        if (typeCompany === 'customer' && newEdl[idEdl].metadatas.status === 'control')
          newEdl[idEdl].metadatas.status = 'assign';
      }

      // Reserialization de la date
      if (newEdl[idEdl].manage?.dateEDL?.seconds) {
        newEdl[idEdl].manage.dateEDL = dayjs(newEdl[idEdl].manage.dateEDL.seconds * 1000).toDate();
      }

      allEdls.current = {
        ...allEdls.current,
        ...newEdl,
      };

      loadedEdls.push(idEdl);
    }

    return loadedEdls.length;
  } catch (error) {
    console.error('Erreur lors du chargement des EDLs du mois:', error);
    return 0;
  }
}

// Fonction pour charger TOUS les EDLs par lots de mois en parallèle
// D'abord les mois futurs (3 mois), puis les mois passés (36 mois max)
// stopLoadingRef permet d'arrêter le chargement en cours
// loadedMonths permet de marquer les mois comme chargés pour le calendrier
async function loadAllEdls(nameCompany, allEdls, setCountEdls, profil, setIsAllEdlsLoaded, setAllEdlsLoadingProgress, setLoadingStats, stopLoadingRef, loadedMonths) {
  const MAX_MONTHS_PAST = 36; // 3 ans d'historique max
  const MAX_MONTHS_FUTURE = 12; // 1 an dans le futur
  const PARALLEL_BATCH_SIZE = 3; // Nombre de mois à charger en parallèle
  const currentMonth = dayjs().startOf('month');
  let totalEdlsLoaded = 0;
  let monthsProcessed = 0;
  let consecutiveEmptyMonths = 0;

  try {
    // Compter le nombre total d'EDLs pour la barre de progression
    const totalEdlsInDb = await countTotalEdls(nameCompany, profil);
    console.log(`Nombre total d'EDLs en base: ${totalEdlsInDb}`);

    // Initialiser les stats avec le total
    setLoadingStats({ loaded: 0, total: totalEdlsInDb, currentMonths: '' });

    // 1. D'abord charger les mois futurs (du plus proche au plus éloigné)
    console.log('Chargement des mois futurs...');
    for (let i = 1; i <= MAX_MONTHS_FUTURE; i += PARALLEL_BATCH_SIZE) {
      // Vérifier si le chargement doit être arrêté
      if (stopLoadingRef.current) {
        console.log('Chargement arrêté par l\'utilisateur');
        setIsAllEdlsLoaded(true);
        return;
      }

      // Préparer les mois futurs à charger dans ce lot
      const monthsToLoad = [];
      for (let j = 0; j < PARALLEL_BATCH_SIZE && (i + j) <= MAX_MONTHS_FUTURE; j++) {
        const month = currentMonth.add(i + j, 'month');
        const monthKey = month.format('YYYY-MM');

        if (!loadedMonths.current.has(monthKey)) {
          monthsToLoad.push({
            month,
            monthKey,
            monthStart: month.toDate(),
            monthEnd: month.endOf('month').toDate(),
          });
        }
      }

      // Afficher les mois en cours de chargement
      const currentMonthsLabel = monthsToLoad.map(m => m.month.format('MMM YYYY')).join(', ');

      // Charger les mois en parallèle
      if (monthsToLoad.length > 0) {
        // Mettre à jour l'affichage AVANT le chargement
        setLoadingStats({ loaded: totalEdlsLoaded, total: totalEdlsInDb, currentMonths: currentMonthsLabel });

        const results = await Promise.all(
          monthsToLoad.map(({ monthStart, monthEnd }) =>
            loadEdlsForMonth(nameCompany, allEdls, profil, monthStart, monthEnd)
          )
        );

        // Traiter les résultats
        results.forEach((edlsCount, idx) => {
          totalEdlsLoaded += edlsCount;
          loadedMonths.current.add(monthsToLoad[idx].monthKey);
        });

        // Mettre à jour la progression
        const progress = totalEdlsInDb > 0 ? Math.round((totalEdlsLoaded / totalEdlsInDb) * 100) : 0;
        setAllEdlsLoadingProgress(progress);
        setLoadingStats({ loaded: totalEdlsLoaded, total: totalEdlsInDb, currentMonths: currentMonthsLabel });
        setCountEdls((prev) => prev + 1);
      }
    }

    // 2. Ensuite charger le mois courant et les mois passés
    console.log('Chargement du mois courant et des mois passés...');
    for (let i = 0; i < MAX_MONTHS_PAST; i += PARALLEL_BATCH_SIZE) {
      // Vérifier si le chargement doit être arrêté
      if (stopLoadingRef.current) {
        console.log('Chargement arrêté par l\'utilisateur');
        setIsAllEdlsLoaded(true);
        return;
      }

      // Préparer les mois à charger dans ce lot
      const monthsToLoad = [];
      for (let j = 0; j < PARALLEL_BATCH_SIZE && (i + j) < MAX_MONTHS_PAST; j++) {
        const month = currentMonth.subtract(i + j, 'month');
        const monthKey = month.format('YYYY-MM');

        if (!loadedMonths.current.has(monthKey)) {
          monthsToLoad.push({
            month,
            monthKey,
            monthStart: month.toDate(),
            monthEnd: month.endOf('month').toDate(),
          });
        }
      }

      // Afficher les mois en cours de chargement
      const currentMonthsLabel = monthsToLoad.map(m => m.month.format('MMM YYYY')).join(', ');

      // Charger les mois en parallèle
      if (monthsToLoad.length > 0) {
        // Mettre à jour l'affichage AVANT le chargement
        setLoadingStats({ loaded: totalEdlsLoaded, total: totalEdlsInDb, currentMonths: currentMonthsLabel });

        const results = await Promise.all(
          monthsToLoad.map(({ monthStart, monthEnd }) =>
            loadEdlsForMonth(nameCompany, allEdls, profil, monthStart, monthEnd)
          )
        );

        // Traiter les résultats
        results.forEach((edlsCount, idx) => {
          totalEdlsLoaded += edlsCount;
          loadedMonths.current.add(monthsToLoad[idx].monthKey);

          if (edlsCount === 0) {
            consecutiveEmptyMonths++;
          } else {
            consecutiveEmptyMonths = 0;
          }
        });
      }

      monthsProcessed += PARALLEL_BATCH_SIZE;

      // Mettre à jour la progression basée sur le nombre d'EDLs chargés vs total
      const progress = totalEdlsInDb > 0 ? Math.round((totalEdlsLoaded / totalEdlsInDb) * 100) : 0;
      setAllEdlsLoadingProgress(progress);
      setLoadingStats({ loaded: totalEdlsLoaded, total: totalEdlsInDb, currentMonths: currentMonthsLabel });
      setCountEdls((prev) => prev + 1);

      // Arrêter si on a 6 mois vides consécutifs (fin de l'historique)
      if (consecutiveEmptyMonths >= 6) {
        console.log('Fin de l\'historique détectée après 6 mois vides');
        break;
      }
    }

    setIsAllEdlsLoaded(true);
    setCountEdls((prev) => prev + 1);
    console.log(`Chargement terminé: ${totalEdlsLoaded} EDLs sur ${monthsProcessed + MAX_MONTHS_FUTURE} mois (dont ${MAX_MONTHS_FUTURE} futurs)`);
  } catch (error) {
    console.error('Erreur lors du chargement de tous les EDLs:', error);
    setIsAllEdlsLoaded(true);
  }
}

function listenEdls(nameCompany, allEdls, setCountEdls, setMessageInfos, profil, startDate, endDate, setIsLoadingMonth, loadedMonths, monthKey) {
  let q = null;
  const typeCompany = profil.company.type;
  let isFirstSnapshot = true;

  const status = ['submit', 'waiting', 'assign', 'control', 'finish'];
  if (['customer', 'admin'].includes(typeCompany)) {
    const baseCollection = collection(dbFirebase, 'edls', nameCompany, 'listEdls');
    if (profil.user.profil !== 'administrator') {
      q = query(
        baseCollection,
        where('metadatas.status', 'in', status),
        where('customer.user.uid', '==', profil.user.uid),
        where('manage.dateEDL', '>=', startDate),
        where('manage.dateEDL', '<=', endDate),
        orderBy('manage.dateEDL', 'desc'),
      );
    } else {
      q = query(
        baseCollection,
        where('metadatas.status', 'in', status),
        where('manage.dateEDL', '>=', startDate),
        where('manage.dateEDL', '<=', endDate),
        orderBy('manage.dateEDL', 'desc'),
      );
    }
  } else if (['provider'].includes(typeCompany)) {
    // Pour les providers, on filtre par dateEDL stocké dans privateP/user
    if (profil.user.profil !== 'administrator') {
      q = query(
        collectionGroup(dbFirebase, 'privateP'),
        where('uid', '==', profil.user.uid),
        where('dateEDL', '>=', startDate),
        where('dateEDL', '<=', endDate),
        orderBy('dateEDL', 'desc'),
      );
    } else {
      q = query(
        collectionGroup(dbFirebase, 'privateP'),
        where('company', '==', profil.company.name),
        where('dateEDL', '>=', startDate),
        where('dateEDL', '<=', endDate),
        orderBy('dateEDL', 'desc'),
      );
    }
  }

  const sub = onSnapshot(
    q,
    async (snapshot) => {
      let changeType = null;
      let edls = [];
      const promises = [];

      for (const change of snapshot.docChanges()) {
        const promise = (async () => {
          const changeType = change.type;
          let idEdl,
            datas,
            docRef = null;
          if (typeCompany === 'provider') {
            if (changeType !== 'removed') {
              const docRefEdl = doc(dbFirebase, change.doc.ref.parent.parent.path);
              const docEdl = await getDoc(docRefEdl);
              datas = docEdl.data();
              docRef = docEdl.ref;
              idEdl = docEdl.id;
            } else idEdl = change.doc.ref.parent.parent.id;
          }
          edls.push({
            changeType: changeType,
            idEdl: idEdl ?? change.doc.id,
            datas: datas ?? change.doc.data(),
            docRef: docRef ?? change.doc.ref,
          });
        })();
        promises.push(promise);
      }

      await Promise.all(promises);

      for (const { changeType, idEdl, datas, docRef } of edls) {
        const newEdl = { [idEdl]: datas };
        if (changeType === 'added' || changeType === 'modified') {
          //START FETCH PRICING
          const listPricing = [];
          if (typeCompany === 'customer') listPricing.push('privateC');
          if (typeCompany === 'admin') listPricing.push('privateC', 'privateP');
          if (typeCompany === 'provider') listPricing.push('privateP');
          const pricingUser = await _getPricing(listPricing, docRef);
          Object.keys(pricingUser).forEach((typeCompany) => {
            newEdl[idEdl][typeCompany] = {
              ...newEdl[idEdl][typeCompany],
              pricing: pricingUser[typeCompany],
            };
          });
          //END FETCH PRICING

          //START FETCH REFUS BY PROVIDER
          let privateAdmin = null;
          if (typeCompany === 'admin') privateAdmin = await _getPrivateInfosAdmin(docRef);
          if (privateAdmin !== null)
            newEdl[idEdl] = {
              ...newEdl[idEdl],
              admin: { refusByProviders: privateAdmin },
            };
          //END FETCH REFUS BY PROVIDER

          if (['customer', 'provider'].includes(typeCompany)) {
            if (newEdl[idEdl].metadatas.status === 'waiting') newEdl[idEdl].metadatas.status = 'submit';
            if (typeCompany === 'customer' && newEdl[idEdl].metadatas.status === 'control')
              newEdl[idEdl].metadatas.status = 'assign';
          }

          //Reserialisation de la date
          newEdl[idEdl].manage.dateEDL = dayjs(newEdl[idEdl].manage.dateEDL.seconds * 1000).toDate();
          // Accumuler dans le cache global (ne pas remplacer)
          allEdls.current = {
            ...allEdls.current,
            ...newEdl,
          };
        } else delete allEdls.current[idEdl];
      }

      // Arrêter le loading après le premier snapshot
      if (isFirstSnapshot) {
        isFirstSnapshot = false;
        if (setIsLoadingMonth) setIsLoadingMonth(false);
      }

      setCountEdls((prevCount) => {
        return prevCount + 1;
      });
      setMessageInfos((prevMessage) => {
        const newCount = prevMessage[0] + 1;
        if (changeType === 'added') return [newCount, 'success', 'Nouvel EDL Soumis'];
        if (changeType === 'modified') return [newCount, 'warning', 'un EDL a été modifié'];
        if (changeType === 'removed') return [newCount, 'error', 'un EDL a été supprimé'];
        return [newCount, 'error', 'inconnu'];
      });
    },
    (error) => {
      console.log(error);
    },
  );

  return sub;
}

const MyEDLs = ({ setEdlToModify, display, setMessageInfos }) => {
  console.log("gerer changement d'annexes avec affichage modification tarification");
  //GESTION DES ETATS
  //Lorsque l'API google maps est chargé, le render est rappelé à cause du useContext
  const { profil } = useContext(AppContext);

  const allEdls = useRef([]);
  const edl = useRef({});
  const [countEdls, setCountEdls] = useState(0);
  const [dialogEdl, setDialogEdl] = useState(false);
  const [status, setStatus] = useState(undefined);
  const [typeView, setTypeView] = useState('calendar');
  const [nameCompany, setNameCompany] = useState(profil.company.name);
  const [provider, setProvider] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedBookingProvider, setAcceptedBookingProvider] = useState(false);
  const [refusedUser, setRefusedUser] = useState(false);
  const [finishProvider, setFinishProvider] = useState(false);
  const [modifyAnnexesOnly, setModifyAnnexesOnly] = useState(false);
  const [dialogCustomerRefuseAfterAssign, setdialogCustomerRefuseAfterAssign] = useState(false);
  const [dialogUpdateDate, setDialogUpdateDate] = useState(false);

  // États pour la pagination par mois (vue calendrier)
  const [currentMonth, setCurrentMonth] = useState(() => dayjs().startOf('month'));
  const [isLoadingMonth, setIsLoadingMonth] = useState(true);
  const loadedMonths = useRef(new Set()); // Mois déjà chargés (clé: "YYYY-MM")
  const oldestLoadedMonth = useRef(null); // Mois le plus ancien chargé (pour charger les mois précédents)

  // États pour la recherche locale
  const [, setSearchText] = useState('');
  const [noResultsFound, setNoResultsFound] = useState(false);

  // États pour le chargement complet de tous les EDLs en arrière-plan
  const [isAllEdlsLoaded, setIsAllEdlsLoaded] = useState(false);
  const [allEdlsLoadingProgress, setAllEdlsLoadingProgress] = useState(0);
  const [loadingStats, setLoadingStats] = useState({ loaded: 0, total: 0 }); // Stats détaillées du chargement
  const isLoadingAllEdls = useRef(false); // Pour éviter les appels multiples
  const stopLoadingRef = useRef(false); // Pour permettre l'arrêt du chargement
  const activeListeners = useRef(new Set()); // Mois avec listeners actifs (clé: "YYYY-MM")

  // Fonction pour obtenir les EDLs filtrés pour le calendrier (mois courant uniquement)
  const getEdlsForCalendar = () => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');

    const filtered = {};
    Object.entries(allEdls.current).forEach(([id, edl]) => {
      if (edl?.manage?.dateEDL) {
        const edlDate = dayjs(edl.manage.dateEDL);
        if (edlDate.isAfter(startOfMonth.subtract(1, 'day')) && edlDate.isBefore(endOfMonth.add(1, 'day'))) {
          filtered[id] = edl;
        }
      }
    });
    return filtered;
  };

  // Fonction pour obtenir tous les EDLs du cache
  const getAllEdlsFromCache = () => {
    return allEdls.current;
  };

  // Utilisation de useEffect pour configurer un listener temps réel sur le mois courant
  // Ce listener est TOUJOURS actif pour détecter les modifications en temps réel
  useEffect(() => {
    if (profil && nameCompany !== 'SKY RENOV') {
      const monthKey = currentMonth.format('YYYY-MM');

      // Vérifier si un listener est déjà actif pour ce mois
      if (activeListeners.current.has(monthKey)) {
        // Listener déjà actif, pas besoin d'en créer un nouveau
        setIsLoadingMonth(false);
        return;
      }

      // Si le mois est déjà chargé (via loadAllEdls), ne pas afficher le loader
      // mais quand même configurer le listener pour les mises à jour en temps réel
      const alreadyLoaded = loadedMonths.current.has(monthKey);
      if (!alreadyLoaded) {
        setIsLoadingMonth(true);
      } else {
        setIsLoadingMonth(false);
      }

      // Calculer les dates de début et fin du mois
      const startDate = currentMonth.toDate();
      const endDate = currentMonth.endOf('month').toDate();

      // Marquer le mois comme ayant un listener actif
      activeListeners.current.add(monthKey);
      loadedMonths.current.add(monthKey);

      // Mettre à jour le mois le plus ancien chargé
      if (!oldestLoadedMonth.current || currentMonth.isBefore(oldestLoadedMonth.current)) {
        oldestLoadedMonth.current = currentMonth;
      }

      const unsub = listenEdls(nameCompany, allEdls, setCountEdls, setMessageInfos, profil, startDate, endDate, alreadyLoaded ? null : setIsLoadingMonth, loadedMonths, monthKey);

      return () => {
        unsub();
        activeListeners.current.delete(monthKey);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profil, nameCompany, setMessageInfos, currentMonth]);

  // Handler pour le changement de mois dans le calendrier
  const handleMonthChange = (newMonth) => {
    const monthStart = dayjs(newMonth).startOf('month');
    if (!monthStart.isSame(currentMonth, 'month')) {
      setCurrentMonth(monthStart);
    }
  };

  // useEffect pour charger TOUS les EDLs en arrière-plan dès le login
  // Cela permet d'avoir le cache complet pour la recherche et la vue liste
  useEffect(() => {
    if (profil && nameCompany !== 'SKY RENOV' && !isLoadingAllEdls.current && !isAllEdlsLoaded) {
      isLoadingAllEdls.current = true;
      stopLoadingRef.current = false; // Réinitialiser le flag d'arrêt
      console.log('Démarrage du chargement de tous les EDLs en arrière-plan...');
      loadAllEdls(nameCompany, allEdls, setCountEdls, profil, setIsAllEdlsLoaded, setAllEdlsLoadingProgress, setLoadingStats, stopLoadingRef, loadedMonths);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profil, nameCompany]);

  // Fonction pour arrêter le chargement des EDLs
  const handleStopLoading = () => {
    stopLoadingRef.current = true;
    console.log('Arrêt du chargement demandé par l\'utilisateur');
  };

  // Handler pour le changement de texte de recherche
  // La recherche se fait uniquement dans le cache local (tous les EDLs sont chargés au login)
  const handleSearchChange = (text) => {
    setSearchText(text);
    setNoResultsFound(false);

    // Si texte vide, réinitialiser
    if (!text) {
      setCountEdls((prev) => prev + 1);
      return;
    }

    // Vérifier si on a des résultats dans le cache local
    const localResults = filterLocalCache(text);
    if (Object.keys(localResults).length === 0) {
      setNoResultsFound(true);
    }

    setCountEdls((prev) => prev + 1);
  };

  // Fonction pour filtrer le cache local selon le texte de recherche
  const filterLocalCache = (text, edlsToFilter = allEdls.current) => {
    const searchLower = text.toLowerCase();
    const filtered = {};

    Object.entries(edlsToFilter).forEach(([id, edl]) => {
      if (!edl || !edl.manage || !edl.possession || !edl.tenants) return;

      const tenant = edl.tenants[0] || {};
      const address = edl.possession.address || {};

      const matches =
        (address.street && address.street.toLowerCase().includes(searchLower)) ||
        (address.complement && address.complement.toLowerCase().includes(searchLower)) ||
        (address.locality && address.locality.toLowerCase().includes(searchLower)) ||
        (address.zipCode && address.zipCode.toLowerCase().includes(searchLower)) ||
        (tenant.name && tenant.name.toLowerCase().includes(searchLower)) ||
        (tenant.surname && tenant.surname.toLowerCase().includes(searchLower)) ||
        (tenant.email && tenant.email.toLowerCase().includes(searchLower)) ||
        (tenant.phone && tenant.phone.toLowerCase().includes(searchLower)) ||
        (edl.manage.typeEDL && edl.manage.typeEDL.toLowerCase().includes(searchLower)) ||
        (edl.possession.typeHouse && edl.possession.typeHouse.toLowerCase().includes(searchLower));

      if (matches) {
        filtered[id] = edl;
      }
    });

    return filtered;
  };

  //GESTION DES HANDLES
  const handleOnClick = ({ edl: _edl }) => {
    // Recherche de l'élément du DOM avec la classe spécifique
    const element = document.querySelector('.fc-popover');
    if (element) element.style.zIndex = '100';
    edl.current = _edl;
    setDialogEdl(true);
    if (edl.current?.metadatas?.status === 'finish') {
      setStatus('finishEdl');
      return;
    }
    switch (profil.company.type) {
      case 'customer':
        if (edl.current?.metadatas?.status === 'submit') {
          setStatus(edl.current?.metadatas?.isModifying ? 'modifiedEdlFromMyEdls' : 'submitEdl');
        }
        if (edl.current?.metadatas?.status === 'assign') {
          edl.current?.metadatas?.isReportUpload
            ? setStatus('controlingEdlCustomer')
            : setStatus('consultAssignEdlCustomer');
        }
        break;
      case 'admin':
        if (['submit', 'waiting', 'assign'].includes(edl.current?.metadatas?.status)) {
          setStatus('assignProvider');
        }
        if (edl.current?.metadatas?.status === 'control') {
          setStatus('controlEdlProvider');
        }
        break;
      case 'provider':
        if (edl.current?.metadatas?.status === 'submit') {
          setStatus('acceptRefuseProvider');
        }
        if (edl.current?.metadatas?.status === 'assign') {
          setStatus('acceptedEdlProvider');
        }
        if (edl.current?.metadatas?.status === 'control') {
          setStatus('waitReportEdlValidation');
        }
        break;
      default:
        // Handle default case here
        break;
    }
  };

  const handleBtnDialog = async ({ btn, motif, newPossession }) => {
    console.log(btn);
    if (btn?.type === 'click') return;

    if (['cancelSubmitEdl', 'cancelModifiedEdl', 'cancelAssignProvider', 'cancelEdlProvider'].includes(btn)) {
      setDialogEdl(false);
      return;
    }

    if (btn === 'canceListProviders') {
      setProvider(false);
      return;
    }
    if (btn === 'cancelBookingProvider') {
      setAcceptedBookingProvider(false);
      return;
    }

    if (btn === 'cancelRefused') {
      setRefusedUser(false);
      return;
    }

    if (btn === 'cancelFinishEdl') {
      setFinishProvider(false);
      return;
    }

    if (['modifiedSubmitEdl', 'modifiedModifiedEdl', 'modifyEdlAdmin'].includes(btn)) {
      setIsLoading(true);
      const newEdl = cloneDeep(edl.current);
      console.log(newEdl);
      newEdl.metadatas.isModifying = true;
      delete newEdl.provider;
      setEdlToModify(newEdl);
      // Pour l'admin système, on utilise le nom de la société du client, pas celle de l'admin
      const companyName = btn === 'modifyEdlAdmin' ? edl.current.customer.company.name : profil.company.name;
      if (['modifiedSubmitEdl', 'modifyEdlAdmin'].includes(btn)) modifyEdl(newEdl.metadatas.idEdl, companyName);
      setDialogEdl(false);
      setIsLoading(false);
      return;
    }

    if (['deleteSubmitEdl', 'deleteModifiedEdl'].includes(btn)) {
      setDialogEdl(false);
      deleteEdl(edl.current.metadatas.idEdl, profil.company.name);
      return;
    }

    if (btn === 'deleteModifiedEdlAssign') {
      setdialogCustomerRefuseAfterAssign(true);
      return;
    }

    if (btn === 'cancelDeleteEdlAssignCustomer') {
      setdialogCustomerRefuseAfterAssign(false);
      return;
    }

    if (btn === 'confirmDeleteEdlAssignCustomer') {
      setDialogEdl(false);
      setdialogCustomerRefuseAfterAssign(false);
      deleteEdl(edl.current.metadatas.idEdl, profil.company.name);
      return;
    }

    if (btn === 'acceptModifiedEdl') {
      setDialogEdl(false);
      const newEdl = cloneDeep(edl.current);
      const pricingCustomer = newEdl.customer.pricing;
      const idEdl = newEdl.metadatas.idEdl;
      delete newEdl.customer;
      delete newEdl.metadatas;
      createEdl(idEdl, newEdl, profil.company.name, pricingCustomer);
      return;
    }

    if (btn === 'showListProvider') {
      setProvider(true);
      setIsLoading(true);
      return;
    }

    if (btn === 'assignlistProviders') {
      setDialogEdl(false);
      setProvider(false);
      waitingEdl(
        edl.current.metadatas.idEdl,
        edl.current.customer.company.name,
        provider.user.uid,
        provider.user.company,
      );
      return;
    }

    if (['acceptEdlProvider', 'modifiedBookingProvider'].includes(btn)) {
      setAcceptedBookingProvider(true);
      return;
    }

    if (btn === 'refuseEdlCustomer') {
      setRefusedUser('refuseMotifEdlCustomer');
      return;
    }

    if (btn === 'refuseEdlProvider') {
      setRefusedUser('refuseBookingProvider');
      return;
    }

    if (btn === 'cancelEdlProvider') {
      setRefusedUser(false);
    }

    if (['acceptBookingProvider', 'refuseBookingProvider'].includes(btn)) {
      setRefusedUser(false);
      setAcceptedBookingProvider(false);
      setDialogEdl(false);
      acceptRefuseEdl(
        btn.startsWith('accept') ? 'accept' : 'refuse',
        edl.current.metadatas.idEdl,
        edl.current.customer.company.name,
        edl.current.manage.dateEDL,
        profil.user.uid,
        profil.user.company,
        edl.current.provider.pricing,
        btn.startsWith('refuse') ? motif : null,
      );
      return;
    }

    if (btn === 'refuseMotifEdlCustomer') {
      setRefusedUser(false);
      setDialogEdl(false);
      refuseEdlByAdmin(edl.current.metadatas.idEdl, edl.current.customer.company.name, motif);
      return;
    }

    if (['finishEdlProvider', 'modifyReportEdlProvider'].includes(btn)) {
      setFinishProvider(true);
      return;
    }

    if (btn === 'modifyAnnexesAdmin') {
      setModifyAnnexesOnly(true);
      setFinishProvider(true);
      return;
    }

    if (btn === 'updateDateEdlAdmin') {
      setDialogUpdateDate(true);
      return;
    }

    if (btn === 'uploadFinishEdl') {
      setFinishProvider(false);
      setModifyAnnexesOnly(false);
      setDialogEdl(false);
      return;
    }

    if (btn === 'cancelFinishEdl') {
      setFinishProvider(false);
      setModifyAnnexesOnly(false);
      return;
    }

    if (btn === 'confirmModifyAnnexes') {
      // Appel API pour modifier les annexes sur un EDL terminé
      if (newPossession) {
        const idEdl = edl.current.metadatas.idEdl;
        const nameCompany = edl.current.customer.company.name;
        // Appeler l'API backend pour mettre à jour les annexes
        modifyAnnexesEdl(idEdl, nameCompany, newPossession);
      }
      setFinishProvider(false);
      setModifyAnnexesOnly(false);
      setDialogEdl(false);
      return;
    }

    if (btn === 'viewReportEdlProvider') {
      let fileURL = null;
      if (process.env.REACT_APP_ENV === 'LOCAL') {
        fileURL = edl.current.metadatas.mediaLink;
      } else {
        const storageRef = ref(storage, edl.current.metadatas.pathReportEdl);
        await getDownloadURL(storageRef).then((downloadURL) => {
          fileURL = downloadURL;
        });
      }
      window.open(fileURL, '_blank');
      return;
    }

    if (btn === 'refuseReportEdlProvider') {
      setRefusedUser('motifReportEdlProvider');
      return;
    }

    if (['acceptReportEdlProvider', 'motifReportEdlProvider'].includes(btn)) {
      setFinishProvider(false);
      setDialogEdl(false);
      acceptRefuseReportEdl(
        btn.startsWith('accept') ? 'accept' : 'refuse',
        edl.current.metadatas.idEdl,
        edl.current.customer.company.name,
      );
    }
  };

  const handleNewEdl = (date) => {
    const newEdl = getDefaultValues();
    newEdl.manage.dateEDL = date;
    setEdlToModify(newEdl);
  };

  const handleUpdateDateClose = (confirmed) => {
    setDialogUpdateDate(false);
  };

  const handleUpdateDateConfirm = async (newDate) => {
    const idEdl = edl.current.metadatas.idEdl;
    const companyName = edl.current.customer.company.name;
    await updateEdlDate(idEdl, companyName, newDate);
    setDialogUpdateDate(false);
    setDialogEdl(false);
  };

  return (
    <Grid container flexGrow={1}>
      {profil.company.type === 'admin' && profil.user.profil === 'administrator' && (
        <Grid sx={{ padding: 1 }} xs={12}>
          <SelectSocietyAdmin setNameCompany={setNameCompany} />
        </Grid>
      )}
      {/* Vue liste : afficher les EDLs progressivement avec indicateur de progression */}
      {profil !== null && typeView === 'items' && (
        <Grid flexGrow={1}>
          <ViewItem
            edls={getAllEdlsFromCache()}
            countEdls={countEdls}
            handleOnClick={handleOnClick}
            typeView={typeView}
            setTypeView={setTypeView}
            display={display}
            hasMoreEdls={false}
            isLoadingMore={false}
            onLoadMore={null}
            onSearchChange={handleSearchChange}
            isSearching={false}
            noResultsFound={noResultsFound}
            isAllEdlsLoaded={isAllEdlsLoaded}
            loadingStats={loadingStats}
            onStopLoading={handleStopLoading}
          />
        </Grid>
      )}
      {profil !== null && typeView === 'calendar' && (
        <Grid flexGrow={1}>
          <ViewCalendar
            edls={getEdlsForCalendar()}
            countEdls={countEdls}
            handleOnClick={handleOnClick}
            typeView={typeView}
            setTypeView={setTypeView}
            handleNewEdl={handleNewEdl}
            typeCompany={profil.company.type}
            onMonthChange={handleMonthChange}
            isLoading={isLoadingMonth && !isAllEdlsLoaded}
          />
        </Grid>
      )}

      {dialogEdl === true ? (
        <DialogSky
          open={true}
          handleDialog={handleBtnDialog}
          status={status}
          isLoading={isLoading}
          datasEdl={edl.current}
          profil={profil}
        >
          <ViewEDL datasEdl={edl.current} profil={profil} display={display} />
        </DialogSky>
      ) : null}

      {provider ? (
        <DialogSky open={true} handleDialog={handleBtnDialog} status="listProviders" isLoading={isLoading}>
          <SelectProviderAdmin datasEdl={edl.current} setProvider={setProvider} setIsLoading={setIsLoading} />
        </DialogSky>
      ) : null}
      {acceptedBookingProvider ? <SelectBookingProvider datasEdl={edl.current} handleDialog={handleBtnDialog} /> : null}
      {refusedUser !== false ? <DialogRefuse status={refusedUser} handleDialog={handleBtnDialog} /> : null}
      {finishProvider && !modifyAnnexesOnly ? (
        <DialogFinishEDL
          datasEdl={edl.current}
          handleDialog={handleBtnDialog}
          priceList={profil.priceList}
          modeAnnexesOnly={false}
        />
      ) : null}
      {finishProvider && modifyAnnexesOnly ? (
        <DialogModifyAnnexesAdmin
          datasEdl={edl.current}
          handleDialog={handleBtnDialog}
        />
      ) : null}

      {dialogCustomerRefuseAfterAssign ? (
        <DialogDeleteAssignEdlCustomer
          datasEdl={edl.current}
          handleDialog={handleBtnDialog}
          pricePenalty={profil.priceList.annulation}
        />
      ) : null}

      {dialogUpdateDate && edl.current ? (
        <DialogUpdateDateAdmin
          open={true}
          handleClose={handleUpdateDateClose}
          currentDate={edl.current.manage.dateEDL}
          onConfirm={handleUpdateDateConfirm}
        />
      ) : null}
    </Grid>
  );
};

export default MyEDLs;
